import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Shield, CheckCircle, ChevronRight } from 'lucide-react';
import BackButton from '../components/ui/BackButton';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AccountSettingsPage = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    matchNotifications: true,
    messageNotifications: true,
  });
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/verification/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVerificationStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch verification status:', error);
      }
    };
    
    if (isAuthenticated) {
      fetchVerificationStatus();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleNotificationToggle = (key) => {
    setNotifications((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleDeactivateAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to deactivate your account? You can reactivate it by logging in again.'
    );
    
    if (!confirmed) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/user/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Account deactivated successfully. You will be logged out.');
      logout();
      navigate('/');
    } catch (error) {
      console.error('Deactivation error:', error);
      alert(error.response?.data?.detail || 'Failed to deactivate account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will PERMANENTLY delete your account and ALL your data including:\n\n' +
      '• Your profile\n' +
      '• All messages\n' +
      '• All interests sent/received\n' +
      '• Your shortlist\n' +
      '• All other account data\n\n' +
      'This action CANNOT be undone!\n\n' +
      'Type "DELETE" to confirm.'
    );
    
    if (!confirmed) return;

    const doubleCheck = window.prompt(
      'Please type DELETE in capital letters to confirm permanent deletion:'
    );
    
    if (doubleCheck !== 'DELETE') {
      alert('Account deletion cancelled.');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${API_URL}/api/user/delete`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Your account has been permanently deleted.');
      logout();
      navigate('/');
    } catch (error) {
      console.error('Deletion error:', error);
      alert(error.response?.data?.detail || 'Failed to delete account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-2 py-8">
        <BackButton />
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Account Settings</h1>

        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <Bell className="w-6 h-6 text-navy-500 mr-3" />
            <h2 className="text-xl font-semibold text-gray-800">Notification Preferences</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <div>
                <p className="font-medium text-gray-800">Email Notifications</p>
                <p className="text-sm text-gray-600">Receive updates via email</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifications.emailNotifications}
                  onChange={() => handleNotificationToggle('emailNotifications')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-mint-500"></div>
              </label>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <div>
                <p className="font-medium text-gray-800">New Match Notifications</p>
                <p className="text-sm text-gray-600">Get notified of new matches</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifications.matchNotifications}
                  onChange={() => handleNotificationToggle('matchNotifications')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-mint-500"></div>
              </label>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-800">Message Notifications</p>
                <p className="text-sm text-gray-600">Get notified of new messages</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifications.messageNotifications}
                  onChange={() => handleNotificationToggle('messageNotifications')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-mint-500"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Identity Verification Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-blue-500 mr-3" />
            <h2 className="text-xl font-semibold text-gray-800">Identity Verification</h2>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {verificationStatus?.govIdVerified ? (
                  <>
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Verified Badge Earned</p>
                      <p className="text-sm text-green-600">Your ID has been verified</p>
                    </div>
                  </>
                ) : verificationStatus?.govIdVerificationStatus === 'pending' ? (
                  <>
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Verification Pending</p>
                      <p className="text-sm text-yellow-600">Your ID is under review</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Get Verified Badge</p>
                      <p className="text-sm text-gray-600">Upload your government ID to get verified</p>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => navigate('/verification')}
                className="flex items-center gap-1 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                {verificationStatus?.govIdVerified ? 'View' : 'Verify Now'}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-lg shadow-md p-6 border-2 border-red-200">
          <h2 className="text-xl font-semibold text-navy-500 mb-4">Danger Zone</h2>
          <div className="space-y-3">
            <button 
              onClick={handleDeactivateAccount}
              disabled={loading}
              className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <p className="font-medium text-gray-800">Deactivate Account</p>
              <p className="text-sm text-gray-600">Temporarily disable your account</p>
            </button>
            <button 
              onClick={handleDeleteAccount}
              disabled={loading}
              className="w-full text-left px-4 py-3 border border-red-300 rounded-lg hover:bg-cream-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <p className="font-medium text-navy-500">Delete Account</p>
              <p className="text-sm text-gray-600">Permanently delete your account and data</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
